/**
 * AI Image Editor export format helpers (PNG / lossless WebP / JPEG).
 * Canvas stays RGB(+alpha); no CMYK mode.
 */

/** @typedef {'png'|'webp'|'jpeg'} ImageEditorFormat */

export const IMAGE_EDITOR_FORMATS = /** @type {const} */ ([
  {
    id: 'png',
    label: 'PNG',
    extension: 'png',
    mimeType: 'image/png',
    hint: 'Lossless + alpha',
  },
  {
    id: 'webp',
    label: 'WebP',
    extension: 'webp',
    mimeType: 'image/webp',
    hint: 'Lossless mode only',
  },
  {
    id: 'jpeg',
    label: 'JPEG',
    extension: 'jpg',
    mimeType: 'image/jpeg',
    hint: 'No alpha — transparency flattened',
  },
]);

/**
 * @param {unknown} raw
 * @returns {ImageEditorFormat}
 */
export function normalizeImageEditorFormat(raw) {
  const v = String(raw || '')
    .trim()
    .toLowerCase();
  if (v === 'webp') return 'webp';
  if (v === 'jpeg' || v === 'jpg') return 'jpeg';
  return 'png';
}

/**
 * @param {ImageEditorFormat|string} format
 * @returns {{ id: ImageEditorFormat, label: string, extension: string, mimeType: string, hint: string }}
 */
export function getImageEditorFormatMeta(format) {
  const id = normalizeImageEditorFormat(format);
  return (
    IMAGE_EDITOR_FORMATS.find((f) => f.id === id) || IMAGE_EDITOR_FORMATS[0]
  );
}

/**
 * Convert physical size to pixels (rounded, clamped by caller).
 * @param {number} value
 * @param {'px'|'cm'|'in'} unit
 * @param {number} dpi
 * @returns {number}
 */
export function physicalSizeToPixels(value, unit, dpi) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 1;
  const d = Number(dpi);
  const safeDpi = Number.isFinite(d) && d > 0 ? d : 72;
  if (unit === 'in') return Math.round(n * safeDpi);
  if (unit === 'cm') return Math.round((n * safeDpi) / 2.54);
  return Math.round(n);
}

/**
 * @param {HTMLCanvasElement} canvas
 * @returns {boolean}
 */
export function canvasHasTransparency(canvas) {
  if (!canvas || !canvas.width || !canvas.height) return false;
  try {
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return false;
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 255) return true;
    }
  } catch {
    return false;
  }
  return false;
}

/**
 * Export canvas to a Blob in the requested format.
 * JPEG flattens onto backgroundColor. WebP uses quality 1 (lossless intent —
 * browsers do not expose a separate lossless flag on canvas.toBlob).
 *
 * @param {HTMLCanvasElement} canvas
 * @param {{
 *   format?: ImageEditorFormat|string,
 *   backgroundColor?: string,
 * }} [opts]
 * @returns {Promise<Blob>}
 */
export function exportCanvasBlob(canvas, opts = {}) {
  const format = normalizeImageEditorFormat(opts.format);
  const meta = getImageEditorFormatMeta(format);
  const backgroundColor =
    typeof opts.backgroundColor === 'string' && opts.backgroundColor.trim()
      ? opts.backgroundColor.trim()
      : '#ffffff';

  return new Promise((resolve, reject) => {
    if (!canvas) {
      reject(new Error('No canvas to export.'));
      return;
    }

    /** @param {Blob|null} blob */
    const finish = (blob) => {
      if (!blob) {
        reject(new Error(`Could not export as ${meta.label}.`));
        return;
      }
      resolve(blob);
    };

    try {
      if (format === 'jpeg') {
        const tmp = document.createElement('canvas');
        tmp.width = canvas.width;
        tmp.height = canvas.height;
        const tctx = tmp.getContext('2d');
        if (!tctx) {
          reject(new Error('Could not create JPEG flatten canvas.'));
          return;
        }
        tctx.fillStyle = backgroundColor;
        tctx.fillRect(0, 0, tmp.width, tmp.height);
        tctx.drawImage(canvas, 0, 0);
        tmp.toBlob(finish, meta.mimeType, 0.92);
        return;
      }
      if (format === 'webp') {
        // Quality 1.0 = lossless intent; do not pass a lossy quality.
        canvas.toBlob(finish, meta.mimeType, 1);
        return;
      }
      canvas.toBlob(finish, meta.mimeType);
    } catch (err) {
      reject(err instanceof Error ? err : new Error(String(err)));
    }
  });
}
